import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { serviceDeliveryAPI, clientPortalAPI } from '../lib/api';
import {
  BarChart3, Loader2, Wand2, Link2, Instagram, Facebook, Megaphone,
  FileDown, Save, Send, Undo2, PenLine, Paperclip, Trash2, Upload, ExternalLink,
} from 'lucide-react';
import { ContentReportView, AdsReportView, PrintStyles, ReportPrintHeader, fmtMonth, toHtml } from './MonthlyReportViews';
import { RichTextEditor } from './RichTextEditor';
import { useAuthStore } from '../store';

const currentMonth = () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };

function StatusBadge({ status }) {
  return status === 'published'
    ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100" data-testid="report-status-badge">Publicado</Badge>
    : <Badge className="bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100" data-testid="report-status-badge">Borrador</Badge>;
}

function TypedReport({ clientId, rtype }) {
  const isContent = rtype === 'content';
  const [loading, setLoading] = useState(true);
  const [linked, setLinked] = useState(false);
  const [mapping, setMapping] = useState(null);
  const [brands, setBrands] = useState([]);
  const [savingMap, setSavingMap] = useState(false);
  const [months, setMonths] = useState([]);
  const [selMonth, setSelMonth] = useState(currentMonth());
  const [report, setReport] = useState(null);
  const [analysis, setAnalysis] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const pdfInputRef = useRef(null);
  const { user } = useAuthStore();
  const canEdit = isContent || user?.role === 'ceo';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let isLinked = false;
      if (isContent) {
        const map = await serviceDeliveryAPI.getMetricoolMapping(clientId);
        setMapping(map);
        isLinked = !!map?.blog_id;
        if (!isLinked && map?.can_edit) {
          const b = await serviceDeliveryAPI.metricoolBrands(clientId).catch(() => ({ brands: [] }));
          setBrands(b?.brands || []);
        }
      } else {
        const client = await serviceDeliveryAPI.getClient(clientId).catch(() => ({}));
        isLinked = !!client?.meta_ad_account_id;
      }
      setLinked(isLinked);
      if (isLinked) {
        const list = await serviceDeliveryAPI.listTypedReports(clientId, rtype);
        const ms = list?.reports || [];
        setMonths(ms);
        if (ms.length > 0) {
          setSelMonth(ms[0].month);
          const rep = await serviceDeliveryAPI.getTypedReport(clientId, rtype, ms[0].month);
          setReport(rep); setAnalysis(toHtml(rep?.analysis || ''));
        } else { setReport(null); setAnalysis(''); }
      }
    } catch (e) { toast.error(e?.message || 'Error cargando informes'); }
    finally { setLoading(false); }
  }, [clientId, rtype, isContent]);
  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const chooseBrand = async (b) => {
    setSavingMap(true);
    try { await serviceDeliveryAPI.setMetricoolMapping(clientId, b.id, b.name); toast.success(`Vinculado a ${b.name}`); await load(); }
    catch (e) { toast.error(e?.message || 'Error al vincular'); }
    finally { setSavingMap(false); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const rep = await serviceDeliveryAPI.generateTypedReport(clientId, rtype, selMonth);
      setReport(rep); setAnalysis(toHtml(rep?.analysis || ''));
      setMonths((m) => m.some((x) => x.month === selMonth)
        ? m.map((x) => x.month === selMonth ? { ...x, status: rep.status, generated_at: rep.generated_at } : x)
        : [...m, { month: selMonth, status: rep.status, generated_at: rep.generated_at }].sort((a, b) => b.month.localeCompare(a.month)));
      toast.success(`Datos de ${fmtMonth(selMonth)} cargados`);
    } catch (e) { toast.error(e?.message || 'No se pudo generar el informe'); }
    finally { setGenerating(false); }
  };

  const openMonth = async (m) => {
    setSelMonth(m);
    try { const rep = await serviceDeliveryAPI.getTypedReport(clientId, rtype, m); setReport(rep); setAnalysis(toHtml(rep?.analysis || '')); }
    catch { setReport(null); setAnalysis(''); }
  };

  const saveAnalysis = async () => {
    if (!report) return;
    setSaving(true);
    try { const rep = await serviceDeliveryAPI.updateTypedReport(clientId, rtype, report.month, { analysis }); setReport(rep); toast.success('Análisis guardado'); }
    catch (e) { toast.error(e?.message || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const togglePublish = async () => {
    if (!report) return;
    const next = report.status === 'published' ? 'draft' : 'published';
    setPublishing(true);
    try {
      const rep = await serviceDeliveryAPI.updateTypedReport(clientId, rtype, report.month, { analysis, status: next });
      setReport(rep);
      setMonths((m) => m.map((x) => x.month === rep.month ? { ...x, status: rep.status } : x));
      toast.success(next === 'published' ? 'Informe publicado: el cliente ya lo ve en su portal' : 'Informe devuelto a borrador: el cliente ya no lo ve');
      if (rep.notify) rep.notify.discord ? toast.success('Aviso enviado al Discord del cliente') : toast.warning(`Sin aviso por Discord: ${rep.notify.reason}`);
    } catch (e) { toast.error(e?.message || 'Error al cambiar el estado'); }
    finally { setPublishing(false); }
  };

  const republish = async () => {
    setPublishing(true);
    try {
      const rep = await serviceDeliveryAPI.updateTypedReport(clientId, rtype, report.month, { analysis, status: 'published' });
      setReport(rep);
      rep.notify?.discord ? toast.success('Informe republicado y aviso enviado al Discord del cliente') : toast.warning(`Republicado, sin aviso por Discord: ${rep.notify?.reason || ''}`);
    } catch (e) { toast.error(e?.message || 'Error al republicar'); }
    finally { setPublishing(false); }
  };

  const onPdfChosen = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f || !report) return;
    if (!/\.pdf$/i.test(f.name)) { toast.error('Solo se admiten archivos PDF'); return; }
    setUploadingPdf(true);
    try { const rep = await serviceDeliveryAPI.uploadReportPdf(clientId, rtype, report.month, f); setReport(rep); toast.success('PDF adjuntado al informe'); }
    catch (err) { toast.error(err?.message || 'Error subiendo el PDF'); }
    finally { setUploadingPdf(false); }
  };

  const removePdf = async () => {
    if (!report || !window.confirm('¿Quitar el PDF de este informe?')) return;
    try { const rep = await serviceDeliveryAPI.deleteReportPdf(clientId, rtype, report.month); setReport(rep); toast.success('PDF eliminado'); }
    catch (err) { toast.error(err?.message || 'Error eliminando el PDF'); }
  };

  if (loading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>;

  if (!linked) {
    if (!isContent) {
      return (
        <Card data-testid="ads-report-not-linked">
          <CardContent className="py-10 text-center space-y-2">
            <Megaphone className="w-8 h-8 mx-auto text-muted-foreground" />
            <p className="text-sm font-medium">Este cliente no tiene vinculada su cuenta publicitaria de Meta.</p>
            <p className="text-sm text-muted-foreground">Vincúlala en la sección <strong>ADS</strong> de la ficha y vuelve aquí para generar su informe de Ads.</p>
          </CardContent>
        </Card>
      );
    }
    return (
      <Card data-testid="reports-mapping">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Link2 className="w-4 h-4 text-primary" /> Vincular con Metricool</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">Elige la marca de Metricool de este cliente para traer sus datos orgánicos reales.</p>
        </CardHeader>
        <CardContent>
          {!mapping?.can_edit ? <p className="text-sm text-muted-foreground italic">Este cliente aún no está vinculado a Metricool. Pídelo a una CEO.</p> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[420px] overflow-y-auto">
              {brands.map((b) => (
                <button key={b.id} disabled={savingMap} onClick={() => chooseBrand(b)} className="text-left rounded-lg border p-3 hover:border-primary transition-colors disabled:opacity-50" data-testid={`report-brand-${b.id}`}>
                  <p className="font-medium text-sm">{b.name}</p>
                  <p className="text-[11px] text-muted-foreground flex gap-2 mt-1">
                    {b.instagram && <span className="inline-flex items-center gap-0.5"><Instagram className="w-3 h-3" />{b.instagram}</span>}
                    {b.facebook && <span className="inline-flex items-center gap-0.5"><Facebook className="w-3 h-3" />FB</span>}
                  </p>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <Card className="print:hidden">
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                {isContent ? <Instagram className="w-4 h-4 text-pink-600" /> : <Megaphone className="w-4 h-4 text-primary" />}
                {isContent ? 'Informe de Contenido (Metricool)' : 'Informe de Ads (Meta)'}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                {isContent ? `Orgánico IG + FB · ${mapping?.brand_name || ''}` : 'Datos directos de Meta Ads, por campaña y en total'}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <input type="month" value={selMonth} onChange={(e) => setSelMonth(e.target.value)} className="h-9 rounded-md border px-2 text-sm" data-testid={`report-month-input-${rtype}`} />
              {canEdit && <Button size="sm" onClick={generate} disabled={generating} data-testid={`report-generate-btn-${rtype}`}>
                {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />} {months.some((x) => x.month === selMonth) ? 'Actualizar datos' : 'Generar informe'}
              </Button>}
              {report && <Button size="sm" variant="outline" onClick={() => window.print()} data-testid={`report-pdf-btn-${rtype}`}><FileDown className="w-4 h-4 mr-1" /> PDF</Button>}
            </div>
          </div>
          {months.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mt-3">
              <span className="text-xs text-muted-foreground">Histórico:</span>
              {months.map((m) => (
                <button key={m.month} onClick={() => openMonth(m.month)} className={`text-xs px-2 py-1 rounded-md border inline-flex items-center gap-1.5 ${m.month === selMonth ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`} data-testid={`report-history-${rtype}-${m.month}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${m.status === 'published' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                  {fmtMonth(m.month)}
                </button>
              ))}
            </div>
          )}
        </CardHeader>
      </Card>

      {!report ? (
        <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground" data-testid={`reports-empty-${rtype}`}>
          Aún no hay informe de este tipo. Elige un mes y pulsa "Generar informe".
        </div>
      ) : (
        <div id="printable-report" className="space-y-5" data-testid={`report-view-${rtype}`}>
          <PrintStyles />
          <ReportPrintHeader title={isContent ? 'Informe de Contenido' : 'Informe de Ads'} month={report.month} />
          <div className="flex items-center justify-between flex-wrap gap-2 print:hidden">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">{fmtMonth(report.month)}</h2>
              <StatusBadge status={report.status} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Datos: {report.generated_at ? new Date(report.generated_at).toLocaleDateString('es-ES') : ''}</span>
              {canEdit && <Button size="sm" variant={report.status === 'published' ? 'outline' : 'default'} onClick={togglePublish} disabled={publishing} data-testid={`report-publish-btn-${rtype}`}>
                {publishing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : (report.status === 'published' ? <Undo2 className="w-4 h-4 mr-1" /> : <Send className="w-4 h-4 mr-1" />)}
                {report.status === 'published' ? 'Volver a borrador' : 'Publicar al cliente'}
              </Button>}
              {canEdit && report.status === 'published' && rtype === 'ads' && (
                <Button size="sm" variant="ghost" onClick={republish} disabled={publishing} title="Vuelve a publicar y avisa de nuevo al cliente por Discord" data-testid={`report-republish-btn-${rtype}`}>
                  <Send className="w-4 h-4 mr-1" />Republicar y avisar
                </Button>
              )}
            </div>
          </div>

          {canEdit && <Card className="print:hidden">
            <CardContent className="py-3 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 text-sm min-w-0">
                <Paperclip className="w-4 h-4 text-muted-foreground shrink-0" />
                {report.pdf ? (
                  <>
                    <span className="font-medium truncate" data-testid={`report-pdf-name-${rtype}`}>{report.pdf.name}</span>
                    <a href={clientPortalAPI.reportPdfUrl(rtype, report.month, clientId)} target="_blank" rel="noreferrer" data-testid={`report-pdf-view-${rtype}`}>
                      <Button size="sm" variant="outline"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Ver PDF</Button>
                    </a>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={removePdf} data-testid={`report-pdf-delete-${rtype}`}><Trash2 className="w-4 h-4" /></Button>
                  </>
                ) : (
                  <span className="text-muted-foreground">Sin PDF adjunto (opcional): si algún mes preparáis el informe en PDF, súbelo aquí y el cliente podrá descargarlo.</span>
                )}
              </div>
              <input ref={pdfInputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onPdfChosen} data-testid={`report-pdf-input-${rtype}`} />
              <Button size="sm" variant="outline" onClick={() => pdfInputRef.current?.click()} disabled={uploadingPdf} data-testid={`report-pdf-upload-btn-${rtype}`}>
                {uploadingPdf ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                {report.pdf ? 'Reemplazar PDF' : 'Adjuntar PDF'}
              </Button>
            </CardContent>
          </Card>}

          <Card className="print-avoid border-primary/30">
            <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><PenLine className="w-4 h-4 text-primary" /> Análisis y mejoras propuestas</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {canEdit ? (
                <>
                  <div className="print:hidden"><RichTextEditor value={analysis} onChange={setAnalysis} minHeight={180} sticky={false} testId={`report-analysis-input-${rtype}`} /></div>
                  <div className="hidden print:block"><RichTextEditor value={analysis} editable={false} testId={`report-analysis-print-${rtype}`} /></div>
                  <div className="flex justify-end print:hidden">
                    <Button size="sm" variant="outline" onClick={saveAnalysis} disabled={saving || analysis === toHtml(report.analysis || '')} data-testid={`report-save-analysis-btn-${rtype}`}>
                      {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar análisis
                    </Button>
                  </div>
                </>
              ) : analysis ? <RichTextEditor value={analysis} editable={false} testId={`report-analysis-view-${rtype}`} />
                : <p className="text-sm text-muted-foreground italic">Sin análisis todavía. Lo escriben las CEOs.</p>}
            </CardContent>
          </Card>

          {isContent ? <ContentReportView data={report.data} /> : <AdsReportView data={report.data} />}
        </div>
      )}
    </div>
  );
}

export default function ReportsSection({ clientId }) {
  return (
    <div className="space-y-4" data-testid="reports-section">
      <div className="flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-semibold">Informes Mensuales</h2>
      </div>
      <Tabs defaultValue="content">
        <TabsList>
          <TabsTrigger value="content" data-testid="reports-tab-content"><Instagram className="w-4 h-4 mr-1.5" /> Informe de Contenido</TabsTrigger>
          <TabsTrigger value="ads" data-testid="reports-tab-ads"><Megaphone className="w-4 h-4 mr-1.5" /> Informe de Ads</TabsTrigger>
        </TabsList>
        <TabsContent value="content" className="mt-4"><TypedReport clientId={clientId} rtype="content" /></TabsContent>
        <TabsContent value="ads" className="mt-4"><TypedReport clientId={clientId} rtype="ads" /></TabsContent>
      </Tabs>
    </div>
  );
}
